/**
 * SampleRow — one row of SamplesCard.
 *
 * Extracted from SamplesCard's `rows.map` so each row can own its own photo
 * query (useSampleRoundPhotos) and lightbox state. The cover thumbnail now
 * reflects the gallery: it shows the round's explicit cover if set, otherwise
 * the first gallery photo, and opens the in-app lightbox on click. When there
 * is no photo yet and the user can edit, the thumbnail becomes an upload
 * affordance so a photo can be added straight from the cover.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import {
  CheckCircle2,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { InlineEdit } from './InlineEdit';
import { SampleQcChecklist } from './SampleQcChecklist';
import { SampleRoundPhotos } from './SampleRoundPhotos';
import { SampleLightbox, type LightboxImage } from './SampleLightbox';
import {
  SAMPLE_STATUSES,
  SAMPLE_STATUS_PILL_CLASS,
  type SampleStatus,
} from '../utils/status-style';
import { useSampleRoundPhotos } from '../hooks/useProductDev';
import {
  useSamplePhotoUpload,
  SAMPLE_PHOTO_ACCEPTED,
} from '../hooks/useSamplePhotoUpload';
import type { RouterOutputs } from '@/lib/trpc';

type SampleRoundRow = RouterOutputs['productDev']['listSamples'][number];

type SamplePatch = Partial<{
  round_label: string;
  requested_at: string | null;
  received_at: string | null;
  fit_notes: string | null;
}>;

interface Props {
  row: SampleRoundRow;
  productDevId: string;
  canEdit: boolean;
  onPatch: (row: SampleRoundRow, patch: SamplePatch) => void;
  onSetStatus: (row: SampleRoundRow, status: SampleStatus) => void;
  onRequestDelete: (id: string) => void;
}

export function SampleRow({
  row,
  productDevId,
  canEdit,
  onPatch,
  onSetStatus,
  onRequestDelete,
}: Props) {
  const { t } = useTranslation('productDev');
  const photos = useSampleRoundPhotos(row.id).data ?? [];
  const { uploading, selectFile } = useSamplePhotoUpload(row.id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Cover-thumb image failed to decode → placeholder icon.
  const [coverError, setCoverError] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Gallery photos that actually have a usable URL.
  const galleryImages: LightboxImage[] = photos
    .filter((p) => Boolean(p.signed_url))
    .map((p) => ({ url: p.signed_url as string, caption: p.caption }));

  // Prepend the explicit cover only when it isn't already one of the gallery
  // URLs, so we never show the same image twice. `coverOffset` maps a gallery
  // index → lightbox index.
  const explicitCoverUrl = row.photo_url ?? null;
  const coverIsDistinct =
    Boolean(explicitCoverUrl) &&
    !galleryImages.some((img) => img.url === explicitCoverUrl);
  const lightboxImages: LightboxImage[] = coverIsDistinct
    ? [
        {
          url: explicitCoverUrl as string,
          alt: t('samples.photoAlt', { label: row.round_label }),
        },
        ...galleryImages,
      ]
    : galleryImages;
  const coverOffset = coverIsDistinct ? 1 : 0;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // What the cover thumbnail displays: explicit cover, else first gallery photo.
  const coverUrl = explicitCoverUrl ?? photos[0]?.signed_url ?? null;
  // Index of the cover image within the lightbox list.
  const coverLightboxIndex = coverIsDistinct
    ? 0
    : coverUrl
      ? lightboxImages.findIndex((img) => img.url === coverUrl)
      : 0;

  return (
    <div className="flex flex-wrap items-start gap-3 py-3 border-b last:border-b-0">
      {/* Cover thumb — clickable when a photo exists (opens the lightbox), or an
          upload affordance when empty + editable, else a static placeholder. */}
      {coverUrl && !coverError ? (
        <button
          type="button"
          onClick={() => openLightbox(Math.max(coverLightboxIndex, 0))}
          className={cn(
            'group relative w-12 h-12 rounded-md border border-border bg-muted overflow-hidden shrink-0',
            'flex items-center justify-center cursor-pointer',
            'transition-opacity duration-200 hover:opacity-90 motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          )}
          aria-label={t('samples.openPhoto')}
          title={t('samples.openPhoto')}
        >
          <img
            src={coverUrl}
            alt={t('samples.photoAlt', { label: row.round_label })}
            className="h-full w-full object-cover"
            onError={() => setCoverError(true)}
          />
          <span className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-200 group-hover:bg-black/10 motion-reduce:transition-none" />
        </button>
      ) : canEdit ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={SAMPLE_PHOTO_ACCEPTED}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void selectFile(f);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="sr-only"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={cn(
              'w-12 h-12 rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/40 shrink-0',
              'flex items-center justify-center text-muted-foreground/50',
              'hover:border-threadheads-purple/40 hover:text-threadheads-purple transition-colors duration-200 motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              uploading && 'cursor-wait',
            )}
            aria-label={t('samples.addPhoto')}
            title={t('samples.addPhoto')}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </button>
        </>
      ) : (
        <div className="w-12 h-12 rounded-md border border-border bg-muted overflow-hidden shrink-0 flex items-center justify-center">
          <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
        </div>
      )}

      {/* Content — min-w-[240px] is the wrap trigger, not decoration: it
          is the width below which the notes and QC review stop being
          readable, so the actions wrap rather than compress past it. */}
      <div className="flex-1 min-w-[240px] space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="min-w-[80px]">
            <InlineEdit
              value={row.round_label}
              onSave={(v) => onPatch(row, { round_label: v ?? row.round_label })}
              placeholder={t('samples.roundLabelPlaceholder')}
              readOnly={!canEdit}
              displayClassName="text-sm font-semibold"
            />
          </div>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              SAMPLE_STATUS_PILL_CLASS[row.status] ??
                'bg-muted text-muted-foreground',
            )}
          >
            {t(`sampleStatus.${row.status}`)}
          </span>
          {row.approved_by_name && row.status === 'approved' && (
            <span className="text-[11px] text-muted-foreground">
              {t('samples.approvedBy', { name: row.approved_by_name })}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="shrink-0">{t('samples.requestedShort')}</span>
            {/* Fits the longest rendered date ("13 Jul 2026") plus the InlineEdit
                button's padding — 80px clipped it to "13 Jul 2…". */}
            <div className="min-w-[104px]">
              <InlineEdit
                type="date"
                value={row.requested_at ? row.requested_at.slice(0, 10) : null}
                onSave={(v) => onPatch(row, { requested_at: v })}
                placeholder="-"
                readOnly={!canEdit}
                display={(v) =>
                  v ? (
                    format(parseISO(v), 'd MMM yyyy')
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )
                }
              />
            </div>
          </span>
          <span className="flex items-center gap-1">
            <span className="shrink-0">{t('samples.receivedShort')}</span>
            {/* Fits the longest rendered date ("13 Jul 2026") plus the InlineEdit
                button's padding — 80px clipped it to "13 Jul 2…". */}
            <div className="min-w-[104px]">
              <InlineEdit
                type="date"
                value={row.received_at ? row.received_at.slice(0, 10) : null}
                onSave={(v) => onPatch(row, { received_at: v })}
                placeholder="-"
                readOnly={!canEdit}
                display={(v) =>
                  v ? (
                    format(parseISO(v), 'd MMM yyyy')
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )
                }
              />
            </div>
          </span>
        </div>
        <InlineEdit
          type="textarea"
          value={row.fit_notes}
          onSave={(v) => onPatch(row, { fit_notes: v })}
          placeholder={t('samples.notesPlaceholder')}
          readOnly={!canEdit}
          displayClassName="text-sm text-muted-foreground"
          rows={2}
        />

        {/* The QC review — what specifically passed and failed on this
            round. Sits under the free-text notes because the notes are
            the round's summary and the checklist is the detail. */}
        <SampleQcChecklist
          productDevId={productDevId}
          sampleRoundId={row.id}
          qcChecks={row.qc_checks}
          canEdit={canEdit}
        />

        {/* Multi-photo gallery per round (additive to the single cover photo
            above). canEdit gates upload + delete. Thumbs open the in-app
            lightbox; the coverOffset keeps gallery index → lightbox index
            correct when an explicit cover is prepended. */}
        <SampleRoundPhotos
          sampleRoundId={row.id}
          canEdit={canEdit}
          onOpenPhoto={(galleryIndex) => openLightbox(galleryIndex + coverOffset)}
        />
      </div>

      {/* Actions — `ml-auto` keeps them right-aligned both inline and on
          their own wrapped line. */}
      {canEdit && (
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {row.status === 'received' && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onSetStatus(row, 'approved')}
                className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t('samples.approve')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onSetStatus(row, 'rejected')}
                className="h-7 px-2 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <XCircle className="h-3.5 w-3.5 mr-1" /> {t('samples.reject')}
              </Button>
            </>
          )}
          <Select
            value={row.status}
            onValueChange={(v) => onSetStatus(row, v as SampleStatus)}
          >
            <SelectTrigger className="h-7 px-2 text-xs w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SAMPLE_STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="text-xs">
                  {t(`sampleStatus.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => onRequestDelete(row.id)}
            className="text-muted-foreground hover:text-destructive p-1"
            aria-label={t('samples.deleteRound')}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <SampleLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        images={lightboxImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
}
